import React from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, MenuItem } from '@mui/material';
import { Project, ProjectStatus } from '../types';
import { useStyles } from '../styles';

import { ReactComponent as AddEmployerIcon } from 'assets/icons/AddEmployerIcon.svg';
import { ReactComponent as DeclineIcon } from 'assets/icons/DeclineIcon.svg';
import { ReactComponent as RegisterIcon } from 'assets/icons/RegisterIcon.svg';
import { ReactComponent as DocumentIcon } from 'assets/icons/DocumentIcon.svg';
import { ReactComponent as ProfileIcon } from 'assets/icons/ProfileIcon.svg';
import { ReactComponent as BadgeIcon } from 'assets/icons/BadgeIcon.svg';

interface ProjectContextMenuProps {
  anchorEl: null | HTMLElement;
  selectedProject: Project | null;
  onClose: () => void;
  onViewProfile: () => void;
  onViewDocuments: () => void;
  onDecline: () => void;
  onAddEmployer: () => void;
  onRegistration: () => void;
  onViewEBadge: () => void;
}

export const ProjectContextMenu: React.FC<ProjectContextMenuProps> = ({
  anchorEl,
  selectedProject,
  onClose,
  onViewProfile,
  onViewDocuments,
  onDecline,
  onAddEmployer,
  onRegistration,
  onViewEBadge,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const renderMenuItems = () => {
    if (!selectedProject) return null;

    const projectStatus =
      selectedProject.projectStatus === 3
        ? ProjectStatus.PAST_PROJECT
        : selectedProject.workerProjectStatus === 1
          ? ProjectStatus.CURRENT_PROJECT
          : ProjectStatus.PENDING_PROJECT;

    switch (projectStatus) {
      case ProjectStatus.PENDING_PROJECT:
        return (
          <>
            <MenuItem data-testid="project-action-register" onClick={onRegistration} className={classes.menuItem}>
              <RegisterIcon />
              {t('projectList.actions.register')}
            </MenuItem>
            <MenuItem data-testid="project-action-decline" onClick={onDecline} className={classes.menuItem}>
              <DeclineIcon />
              {t('projectList.actions.decline')}
            </MenuItem>
          </>
        );

      case ProjectStatus.PAST_PROJECT:
        return (
          <>
            <MenuItem data-testid="project-action-view-profile" onClick={onViewProfile} className={classes.menuItem}>
              <ProfileIcon />
              {t('projectList.actions.viewProfile')}
            </MenuItem>
            <MenuItem
              data-testid="project-action-view-documents"
              onClick={onViewDocuments}
              className={classes.menuItem}
            >
              <DocumentIcon />
              {t('projectList.actions.viewDocuments')}
            </MenuItem>
            <MenuItem data-testid="project-action-view-ebadge" onClick={onViewEBadge} className={classes.menuItem}>
              <BadgeIcon />
              {t('projectList.actions.viewEBadge')}
            </MenuItem>
          </>
        );

      case ProjectStatus.CURRENT_PROJECT:
        return (
          <>
            <MenuItem data-testid="project-action-view-profile" onClick={onViewProfile} className={classes.menuItem}>
              <ProfileIcon />
              {t('projectList.actions.viewProfile')}
            </MenuItem>
            <MenuItem
              data-testid="project-action-view-documents"
              onClick={onViewDocuments}
              className={classes.menuItem}
            >
              <DocumentIcon />
              {t('projectList.actions.viewDocuments')}
            </MenuItem>
            <MenuItem data-testid="project-action-add-employer" onClick={onAddEmployer} className={classes.menuItem}>
              <AddEmployerIcon />
              {t('projectList.actions.addEmployer')}
            </MenuItem>
            <MenuItem data-testid="project-action-view-ebadge" onClick={onViewEBadge} className={classes.menuItem}>
              <BadgeIcon />
              {t('projectList.actions.viewEBadge')}
            </MenuItem>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      className={classes.contextMenu}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      {renderMenuItems()}
    </Menu>
  );
};
